import {Vue} from 'vue-property-decorator';
import config from '@/config';
import {LooseObject} from '@/types';
import dayjs from 'dayjs';
import moment from 'moment';

/**
 * "moment" filter. Convert value into moment.js object.
 *
 * @param value string
 * @param args
 * @return Moment
 */
export function toMoment(value: string | Date, ...args: any) {
  return dayjs(value, ...args);
}
/**
 * "moment" filter. Convert value into moment.js object.
 *
 * @param value string
 * @return Moment
 */
export function validDate(value: string | Date) {
  return moment(moment().format('YYYY-MM-DD')).isBefore(value);
}

/**
 * Convert value to formatted date string.
 *
 * @param value string
 * @param defaultValue
 * @return string
 */
export function toDate(value: string | Date, defaultValue = '-') {
  if (!value) return value || defaultValue;

  return dayjs(value).format(config.dateFormat);
}

/**
 * Convert value to formatted date string.
 *
 * @param value string
 * @param inputFormat
 * @param defaultValue
 * @return string
 */
export function timeFormat(
  value: string | Date,
  inputFormat = 'HH:mm:ss',
  defaultValue = '-',
) {
  if (!value) return value || defaultValue;
  return dayjs(value, inputFormat)
    .utc()
    .format(config.timeFormat);
}
export function timeMoment(
  value: string | Date,
  inputFormat = 'HH:mm:ss',
  defaultValue = '-',
) {
  if (!value) return value || defaultValue;
  return moment.utc(value, inputFormat).format(config.timeFormat);
}
/**
 * Operational Hour string format.
 *
 * @param openHour
 * @param closeHour
 * @param inputFormat
 * @param defaultValue
 */
export function operationalHour(
  openHour: string,
  closeHour: string,
  inputFormat = undefined,
  defaultValue = '',
) {
  const open = timeMoment(openHour, inputFormat, defaultValue);
  const close = timeMoment(closeHour, inputFormat, defaultValue);
  return `${open} - ${close}`;
}

/**
 * Convert value to formatted date time string.
 *
 * @param value string
 * @param defaultValue
 * @return string
 */
export function toDateTime(value: string | Date, defaultValue = '-') {
  if (!value) return value || defaultValue;

  return dayjs(value).format(config.dateTimeFormat);
}

/**
 * Convert status text from number to formatted text.
 *
 * @param status
 * @param activeText
 * @param notActiveText
 */
export function statusText(
  status: number,
  activeText = 'Active',
  notActiveText = 'Not Active',
) {
  return status === 1 ? activeText : notActiveText;
}
// duration
export function timeDutation(value: number) {
  const num = value;
  const hours = num / 60;
  const rhours = Math.floor(hours);
  const minutes = (hours - rhours) * 60;
  if (num === 0) {
    return 'less than 1 minute';
  } else if (num > 0 && num < 60) {
    return minutes + ' minutes';
  } else if (num > 60) {
    return rhours + ' hour(s)';
  } else {
    return num;
  }
}

const filters: LooseObject = {
  toMoment,
  validDate,
  toDate,
  toDateTime,
  timeFormat,
  timeDutation,
  statusText,
};

Object.keys(filters).forEach(name => {
  Vue.filter(name, filters[name]);
});
